
<script setup>
import { reactive, computed } from 'vue';
import { useRoute } from 'vue-router';
import router from '@/router';

import CodesRegularVue from './CodesRegular.vue';
import CodesTreatmentVue from './CodesTreatment.vue';
import CodesCounsellingVue from './CodesCounselling.vue';
import CodesPremiumsVue from './CodesPremiums.vue';

import CodeDisplay from './CodeDisplay.vue';

import PatientSelectVue from '@/appointment/AppointmentPatientSelect.vue';
import AppointmentInfoVue from './AppointmentInfo.vue';

import { getAppointments, newAppointment, updateAppointment, getAppointmentById, deleteAppointment, getAppointmentsForDate } from '../firebase_files/appointment_functions';
import { getPatient } from "../firebase_files/patient_functions"
import { Appointment } from '@/libraries/ClassDefinitions';
import { masterList, updateXCodeObjects, getXCodeObjects, clearXCodes } from '@/libraries/RadiationTreatmentObjects';
import { getSpecialVisitCode, getDetention } from '@/billing/premiums/PremiumScript';
import { toggleCorrectNavigationOption } from '@/store';

const route = useRoute();

const apptId = route.params.id;
const canDelete = !!(apptId && apptId.substring(0, 6) != 'clinic');

const codeTabs = [
  { name: 'regular', label: 'Regular' },
  { name: 'treatment', label: 'Treatment Plans' },
  { name: 'counselling', label: 'Counselling' },
];

const state = reactive({
  apptId: apptId,
  patient: null,
  newApt: new Appointment,
  showQuestions: [],
  appointments: null,
  regularCodeModel: [],
  treatmentCodeModel: [],
  counsellingCodeModel: [],
  specialVisitCodeModel: [],
  detentionCodeModel: [],
  showPatientSelect: false,
  selectedCodeTab: codeTabs[0],
  timePeriodAppointments: [],
  clinicCr: null, // defined iff isClinic, to be passed into patient select, initialized in initialize()

})

// ALL CODES
const totalCodeModel = computed(() => {
  const diseasePremium = validateDiseases(); //returns code object or false
  if (diseasePremium) {
    state.regularCodeModel.push(diseasePremium);
  }
  state.detentionCodeModel = getDetention(state.newApt.length, state.regularCodeModel[0]); // returns empty array on fail
  state.specialVisitCodeModel = getSpecialVisitCode(state.newApt, state.timePeriodAppointments);
  return [].concat(...state.regularCodeModel, state.treatmentCodeModel, state.counsellingCodeModel, state.specialVisitCodeModel, state.detentionCodeModel);
})
const totalPrice = computed(() => {
  let price = 0;
  totalCodeModel.value.forEach(codeObject => { price += codeObject.price })
  return price;
})
// we show the length question if we billed a regular code
const showLengthQuestion = computed(() => {
  return (state.regularCodeModel.length);
})

// once user adds x codes, stop clearing them everytime they open that component (guard clause checks this prop)
const shouldClearXCodes = computed(() => {
  return (state.treatmentCodeModel.length == 0);
})


toggleCorrectNavigationOption();
initialize();

async function initialize() {
  masterList.forEach(radOncList => { // set to false to clear values from last apt
    radOncList.forEach(codeObject => {
      codeObject.isSelected = false;
    })
  })
  const r = route;
  const a = r.params;
  if (apptId) { // editing apt or clinic
    state.newApt = await getAppointmentById(apptId);
    let p = await getPatient(state.newApt.cr);
    if (!p || !p.length) { // create patient for first time in clinic
      state.clinicCr = state.newApt.cr;
      state.showPatientSelect = true;
    }
    selectSavedCodes();
  }

  else { // if not editing or clinc, patient lookup required
    state.showPatientSelect = true;
    selectSavedCodes();
  }

}

function onCancel() {
  router.back();
}


/**
 * Handles previously selected X codes...still need to do A codes
 */
function selectSavedCodes() {

  if (!apptId) return;
  // clear from fucking run time because js is retarded and doest wipe masterlist when the page changes
  clearXCodes(); 
  // use firebase code strings to populate masterList variables
  updateXCodeObjects(state.newApt.codes, masterList, false); 
  state.treatmentCodeModel = getXCodeObjects();

}


/**
 * When we find a patient or create the patient, get's appointments
 * @param p 
 */
async function initializePatient(p) {
  state.newApt.patientName = p.name; // apts need name for display in HomeView
  state.patient = p;
  state.newApt.cr = p.cr;
  // filter for only real appts, and old appts had code prop instead of codes so lose those too
  let appointments = await getAppointments(p.cr)
  let timePeriodAppointments = (await getAppointmentsForDate(state.newApt.date)).filter(apt => apt.hasOwnProperty('aptType'));
  if (apptId) {
    appointments = appointments.filter(apt => apt.uniqueAppointmentId != apptId); // remove current appt
    timePeriodAppointments = timePeriodAppointments.filter(apt => apt.uniqueAppointmentId != apptId); // remove current appt
  }
  state.appointments = appointments;
}

async function saveApt() {
  if (!totalCodeModel.value.length) { // no codes billed
    alert('You cannot bill an appointment with no codes.\nEither find codes to bill, or discard the appointment.');
    return;
  }
  if (!(state.newApt.date && state.newApt.time && state.newApt.diagnosis)) {
    alert('Make sure date, end time, patient type, and diagnosis are all filled in.');
    return;
  }

  let price = 0;
  let codes = [];
  totalCodeModel.value.forEach(codeObject => {
    price += codeObject.price;
    codes.push(codeObject.code)
  })

  state.newApt.price = price;
  state.newApt.codes = codes;
  if (apptId) { // if editing apt, update
    await updateAppointment(state.newApt);
  } else { // if new apt, write to firebase
    await newAppointment(state.newApt);
  }
  router.push({ name: 'home' });
}

async function deleteApt() {
  if (confirm("Are you sure you want to delete this appointment?\nThis action cannot be undone, but you can always rebill the appointment.")) {
    deleteAppointment(state.newApt);
    router.push({ name: 'home' });
  }
}

/**
 * @returns {String} if E078 is valid returns 'E078 + <disease code>', false otherwise
 */
function validateDiseases() {
  try { // defensive programming...if something were to go wrong it would be one of these 2 properties being undefined
    let x = [state.regularCodeModel[0].code.substring(1), state.patient.diseases]
  } catch (err) {
    return false;
  }

  let violated = false;
  totalCodeModel.value.forEach(codeObject => {
    const subString = codeObject.code.substring(0, 4);
    if (subString == 'E078') {
      violated = true;
    }
  })
  if (violated) return false;

  const validSuffixes = ['340', '341', '343'];
  const aptCodeSuffix = state.regularCodeModel[0].code.substring(1);
  let price;
  switch (aptCodeSuffix) {
    case '340':
      price = 59.55 / 2; // HARD CODED
      break;
    case '341':
      price = 68.90 / 2;
      break;
    case '343':
      price = 77.55 / 2;
      break;
  }
  try {
    // super hacky code so errors aren't thrown when old apts w/ no disease come through ... wipe firebase to remove
    if (validSuffixes.includes(aptCodeSuffix) && state.patient.diseases.length) {
      const code = state.patient.diseases[0].code;
      return {
        code: ("E078 +" + code),
        price: price,
        name: 'Disease Premium (Adds 50% to Appointment code)',
      };

    } else {
      return false;
    }
  } catch (err) {
    return false;
  }
}


/**
 * Handles emit from PatientSelection and Clinic, updates display, calls appts retrieval and sets state.patient.
 * @param patient patient object retrieved from firebase
 */
function patientFound(patient) {
  state.patient = patient;
  state.showPatientSelect = false;
  initializePatient(patient);
}

function openPatient() {
  state.showPatientSelect = true;
}


function updateXCodes(xCodes) {
  state.treatmentCodeModel = xCodes;
}

</script>
<template>
  <div class="page-title">
    <div class="title">Appointment</div>
    <div>
      <button v-if="canDelete" @click="deleteApt()" class="danger alt">Delete</button>&nbsp;
      <button @click="router.back()" class="alt">Cancel</button>&nbsp;
      <button @click="saveApt" class="primary">Save</button>
    </div>
  </div>

  <div class="container" @click="test12">
    <div class="left">

      <PatientSelectVue v-if="state.showPatientSelect" @cancel="onCancel()" @setPatient="patientFound"
        :existingPatient=state.patient :clinicPatientCr="state.clinicCr" :apt-id="state.apptId">
      </PatientSelectVue>

      <div v-if="state.patient">    
        <AppointmentInfoVue :new-apt="state.newApt" :patient="state.patient"
        :show-questions="state.showQuestions"  :showLengthQuestion="showLengthQuestion" @open-patient="openPatient"/>

        <CodeDisplay :totalCodeModel="totalCodeModel" :totalPrice="totalPrice"/>
      </div> 

    </div>
    <div class=" right">
      <div v-if="state.patient && state.appointments" class="content-box">
        <div class="code-tabs">
          <div v-for="ct in codeTabs" :key="ct.name" @click="state.selectedCodeTab = ct"
            :class="{ selected: state.selectedCodeTab.name == ct.name }">{{ ct.label }}</div>
        </div>

        <!-- REGULAR APPOINTMENT -->
        <CodesRegularVue v-if="state.selectedCodeTab.name == 'regular'" :newApt="state.newApt" :patient="state.patient"
          :appointments="state.appointments" @onCodeSelect="cm => state.regularCodeModel = cm"
          @onValidate="questions => state.showQuestions = questions">
        </CodesRegularVue>

        <!-- <CodesTreatmentVue v-if="state.selectedCodeTab.name == 'treatment'" :newApt="state.newApt"
          :clearXCodes="shouldClearXCodes" @onCodeSelect="cm => state.treatmentCodeModel = cm"
          @requiredQuestions="questions => state.showQuestions = questions"></CodesTreatmentVue> -->

          <CodesTreatmentVue v-if="state.selectedCodeTab.name == 'treatment'" 
          :all-codes="totalCodeModel" @emit-codes="(xCodes)=>updateXCodes(xCodes)" />

        <CodesCounsellingVue v-if="state.selectedCodeTab.name == 'counselling'" :appointments="state.appointments"
          :newApt="state.newApt" :totalCodeModel="totalCodeModel"
          @onCodeSelect="cm => { state.counsellingCodeModel = cm;  }">
        </CodesCounsellingVue>

        <CodesPremiumsVue v-if="state.selectedCodeTab.name == 'premiums'" :newApt="state.newApt"
          :timePeriodAppointments="state.timePeriodAppointments"></CodesPremiumsVue>

      </div>

    </div>
  </div>
</template>
<style lang="scss" scoped>

div.buttons {
  text-align: center;
  padding-top: 20px;
}
// putting this in styles

div.container {
  max-width: 1000px;
  display: flex;
  margin: auto;

  .left {
    flex: 1;
    margin-right: 10px;
  }

  .right {
    flex: 1;
    margin-left: 10px;
  }
}

div.code-tabs {
  display: flex;
  justify-content: space-around;
  margin-bottom: 10px;
  border-bottom: solid 1px #ccc;

  &>div {
    padding: 3px 10px;
    font-size: 90%;

    &.selected {
      border-bottom: solid 2px #673ab7;
      font-weight: bold;
    }

    &:not(.selected):hover {
      cursor: pointer;
      color: #673ab7;
    }
  }
}
</style>